import React from "react";
import { Box, Typography, Select, MenuItem } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { YOUTUBE_CATEGORIES, YOUTUBE_PRIVACY } from "../constants";

const PRIVACY_OPTIONS = [
  { value: YOUTUBE_PRIVACY.PUBLIC, label: "Public", Icon: PublicIcon },
  { value: YOUTUBE_PRIVACY.UNLISTED, label: "Unlisted", Icon: LinkIcon },
  { value: YOUTUBE_PRIVACY.PRIVATE, label: "Private", Icon: LockIcon },
];

const YouTubeSettings = ({
  privacy,
  categoryId,
  onPrivacyChange,
  onCategoryChange,
  titleLength,
  textLength,
}) => {
  return (
    <Box className="post-card post-card-sm">
      {/* Header */}
      <Box
        className="post-card-header post-card-header-sm"
        sx={{ gap: "8px" }}>
        <YouTubeIcon sx={{ color: "#FF0000", fontSize: 20 }} />
        <Typography className="post-card-title">YouTube Settings</Typography>
      </Box>

      <Box
        sx={{
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}>

        {/* Video-only notice */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            backgroundColor: "rgba(255,0,0,0.07)",
            borderRadius: "8px",
            padding: "10px 12px",
            border: "1px solid rgba(255,0,0,0.18)",
          }}>
          <VideoLibraryIcon
            sx={{ color: "#FF0000", fontSize: 15, mt: "2px", flexShrink: 0 }}
          />
          <Typography
            sx={{
              fontSize: "12px",
              color: "var(--text-color)",
              opacity: 0.85,
              lineHeight: 1.55,
            }}>
            YouTube only accepts <strong>video files</strong> (MP4, MOV, AVI…).
            Images will be rejected.
          </Typography>
        </Box>

        {/* Visibility */}
        <Box>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "var(--text-color)",
              opacity: 0.55,
              mb: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
            }}>
            Visibility
          </Typography>
          <Box sx={{ display: "flex", gap: "8px" }}>
            {PRIVACY_OPTIONS.map(({ value, label, Icon }) => {
              const isSelected = privacy === value;
              return (
                <Box
                  key={value}
                  onClick={() => onPrivacyChange(value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "6px 12px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    border: isSelected
                      ? "1px solid #FF0000"
                      : "1px solid var(--border-color)",
                    backgroundColor: isSelected
                      ? "rgba(255,0,0,0.1)"
                      : "transparent",
                    color: isSelected ? "#FF0000" : "var(--text-color)",
                    opacity: isSelected ? 1 : 0.55,
                    fontSize: "12px",
                    fontWeight: isSelected ? 600 : 400,
                    transition: "all 0.15s",
                    userSelect: "none",
                    "&:hover": {
                      opacity: 1,
                      borderColor: "#FF0000",
                      color: "#FF0000",
                    },
                  }}>
                  <Icon sx={{ fontSize: 13 }} />
                  {label}
                </Box>
              );
            })}
          </Box>
          {privacy !== YOUTUBE_PRIVACY.PRIVATE && (
            <Typography
              sx={{
                fontSize: "11px",
                color: "var(--text-color)",
                opacity: 0.4,
                mt: "7px",
                lineHeight: 1.5,
              }}>
              Note: until Google API Audit is passed, all videos are published
              as <strong>Private</strong> by Google regardless of this setting.
            </Typography>
          )}
        </Box>

        {/* Category */}
        <Box>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "var(--text-color)",
              opacity: 0.55,
              mb: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
            }}>
            Category
          </Typography>
          <Select
            value={categoryId}
            onChange={(e) => onCategoryChange(e.target.value)}
            size="small"
            fullWidth
            MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}
            sx={{
              fontSize: "13px",
              color: "var(--text-color)",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--border-color)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF0000",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF0000",
              },
              "& .MuiSvgIcon-root": { color: "var(--text-color)" },
            }}>
            {YOUTUBE_CATEGORIES.map(({ id, label }) => (
              <MenuItem key={id} value={id} sx={{ fontSize: "13px" }}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Character limits for YouTube */}
        <Box>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "var(--text-color)",
              opacity: 0.55,
              mb: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
            }}>
            YouTube Limits
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <Typography
                sx={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.6 }}>
                Title
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color:
                    titleLength > 100
                      ? "error.main"
                      : titleLength > 80
                      ? "#FF8C00"
                      : "var(--text-color)",
                  opacity: titleLength > 80 ? 1 : 0.45,
                }}>
                {titleLength} / 100
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <Typography
                sx={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.6 }}>
                Description
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color:
                    textLength > 5000
                      ? "error.main"
                      : textLength > 4500
                      ? "#FF8C00"
                      : "var(--text-color)",
                  opacity: textLength > 4500 ? 1 : 0.45,
                }}>
                {textLength} / 5000
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Shorts auto-detect info */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "7px" }}>
          <InfoOutlinedIcon
            sx={{
              fontSize: 13,
              color: "var(--text-color)",
              opacity: 0.35,
              mt: "2px",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: "11px",
              color: "var(--text-color)",
              opacity: 0.4,
              lineHeight: 1.55,
            }}>
            Shorts are auto-detected (vertical, 15–60 s). Add{" "}
            <strong>#Shorts</strong> to force.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default YouTubeSettings;
